import React from 'react';
import { Avatar, Flex, Group, Title, UnstyledButton, Menu, rem, Text, Anchor, Burger, Drawer, Stack } from '@mantine/core';
import { Logo } from '../ui/Logo';
import { Link, router, usePage } from '@inertiajs/react';
import { IconCaretDownFilled, IconLogout, IconUser } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useDisclosure } from '@mantine/hooks';
import { AppVersion } from '../ui';
import { useTranslation } from 'react-i18next';
import { WhatsNew } from '../ui/WhatsNew';

export function openLogoutConfirm(t, logoutUrl) {
    modals.openConfirmModal({
        title: t('header.logoutConfirm'),
        confirmProps: { color: 'red', leftSection: <IconLogout /> },
        labels: { confirm: t('header.logout'), cancel: t('common.cancel') },
        onCancel: () => console.log('Cancel'),
        onConfirm: () => router.post(logoutUrl || '/logout'),
    });
}

export function Header({ logoProps = {}, menuItems = [], withWhatsNew = false, withBurger = true, withPhoneUserMenu = true, ...props }) {

    const { t } = useTranslation();
    const [opened, { toggle, close }] = useDisclosure();
    // auth is null on pages that are reachable without logging in, such as the
    // public changelog (AD-6).
    const { auth } = usePage().props;
    const { user } = auth ?? {};

    const MenuLink = ({ name, url, isReact, active, ...anchorProps }) => (
        <Anchor component={isReact ? Link : 'a'} fw={active ? "bold" : "500"} fz="lg" c={active ? "dark" : "gray"} underline="never" href={url} {...anchorProps}>{name}</Anchor>
    )

    const dropdownProps = { withArrow: true, width: 200, shadow: "md", radius: "md" };

    const MenuItems = ({ mobile = false }) => (
        menuItems.map((item, index) => {
            const key = `header-item-${index}`;
            const { name, items } = item;
            const onNavigate = mobile ? close : undefined;

            if (!items?.length) {
                return <MenuLink key={key} {...item} onClick={onNavigate} />;
            }

            const active = item.active || items.some(({ active }) => active);

            if (mobile) {
                return (
                    <Stack key={key} gap="xs">
                        <Text fw={active ? "bold" : "500"} fz="lg" c={active ? "dark" : "gray"}>{name}</Text>
                        <Stack gap="xs" pl="md">
                            {items.map((child, childIndex) => (
                                <MenuLink key={`${key}-${childIndex}`} fz="md" {...child} onClick={onNavigate} />
                            ))}
                        </Stack>
                    </Stack>
                );
            }

            return (
                <Menu key={key} {...dropdownProps} trigger="click-hover">
                    <Menu.Target>
                        <UnstyledButton>
                            <Group gap={2} align="center">
                                <Text fw={active ? "bold" : "500"} fz="lg" c={active ? "dark" : "gray"}>{name}</Text>
                                <IconCaretDownFilled size="0.8rem" />
                            </Group>
                        </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                        {items.map(({ name, url, isReact, active }, childIndex) => (
                            <Menu.Item key={`${key}-${childIndex}`} component={isReact ? Link : 'a'} href={url} fw={active ? "bold" : undefined}>
                                {name}
                            </Menu.Item>
                        ))}
                    </Menu.Dropdown>
                </Menu>
            );
        })
    )

    return (
        <>
            <Drawer opened={opened} onClose={close} title={t('header.menu')}>
                <Stack>
                    <MenuItems mobile />
                </Stack>
            </Drawer>

            <Group h="100%" px="md" justify="space-between">
                <Group gap={4}>
                    {withBurger && <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />}
                    <Group>
                        <Anchor {...logoProps.link}>
                            <Logo type={logoProps.type || 'crm'} size={30} h={{ base: 32, sm: 25, md: 35 }} />
                        </Anchor>
                      {/*  <AppVersion /> */}
                    </Group>
                </Group>

                <Group gap="lg" visibleFrom="sm">
                    <MenuItems />
                </Group>

                <Group gap={10} wrap="nowrap">
                    {withWhatsNew && <WhatsNew />}

                    {user && (
                        <Menu {...dropdownProps}>
                            <Menu.Target>
                                <UnstyledButton visibleFrom={withPhoneUserMenu ? undefined : 'sm'}>
                                    <Group>
                                        <Avatar color="initials" size="md" name={user?.name} />
                                        <Flex align="center" gap={2}>
                                            <Title size={16} fw="normal" visibleFrom="sm">{user?.name}</Title>
                                            <IconCaretDownFilled size="1rem" />
                                        </Flex>
                                    </Group>
                                </UnstyledButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Label hiddenFrom="sm">{user?.name}</Menu.Label>
                                <Menu.Item leftSection={<IconUser style={{ width: rem(14), height: rem(14) }} />} component={Link} href={props.profileUrl || '/profile'}>
                                    {t('header.profile')}
                                </Menu.Item>
                                <Menu.Item
                                    leftSection={<IconLogout style={{ width: rem(14), height: rem(14) }} />}
                                    onClick={() => openLogoutConfirm(t, props.logoutUrl)}
                                >
                                    {t('header.logout')}
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    )}
                </Group>
            </Group>
        </>
    )
}